import type { CSSProperties, RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { SurfLayer, SurfStyle } from "@vida/surf-core";
import {
  applyTextCase,
  bakedTextAltText,
  buildChartRenderPlan,
  DEFAULT_SLIDE_HEIGHT_PX,
  getLayerBakedText,
  getLayerChart,
  getLayerCrop,
  getLayerTable,
  getLayerTextRender,
  getRasterBackdrop,
  getTextBoxBackground,
  isTextRasterLayer,
  isTextRasterStale,
  layerRenderStyle,
  resolveFittedTextStyle,
  resolveTextStyle,
  snapSurfFontWeight,
  textRasterAltText,
  usesRasterSolidPanel,
} from "@vida/surf-core";
import { useNormDrag } from "../../hooks/useNormDrag";
import {
  createWebTextLayoutContext,
  layoutLayerTextLines,
} from "../../utils/surfTextMeasure";
import { GraphicLayerPreview } from "./GraphicLayer";

function useSlideHeightPx(containerRef: RefObject<HTMLDivElement | null>): number {
  const [height, setHeight] = useState(DEFAULT_SLIDE_HEIGHT_PX);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setHeight(el.clientHeight || DEFAULT_SLIDE_HEIGHT_PX);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);
  return height;
}

function TableLayerPreview({ layer, theme }: { layer: SurfLayer; theme: SurfStyle }) {
  const data = getLayerTable(layer);
  const bodyStyle = resolveTextStyle({ ...layer, type: "text", textRole: "body" }, theme);
  return (
    <div className="editor-layer-table">
      <table>
        <thead>
          <tr>
            {data.columns.map((col, i) => (
              <th
                key={`h-${i}`}
                style={{
                  background: theme.palette.muted,
                  color: theme.palette.text,
                  fontFamily: bodyStyle.font,
                  fontWeight: 700,
                }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            <tr key={`r-${ri}`}>
              {data.columns.map((_, ci) => (
                <td
                  key={`c-${ri}-${ci}`}
                  style={{
                    color: theme.palette.text,
                    fontFamily: bodyStyle.font,
                    fontWeight: bodyStyle.weight,
                  }}
                >
                  {row[ci] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartLayerPreview({ layer, theme }: { layer: SurfLayer; theme: SurfStyle }) {
  const plan = buildChartRenderPlan(getLayerChart(layer), theme);
  return (
    <div className="editor-layer-chart">
      <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="editor-layer-chart-svg" aria-hidden>
        {plan.polygons.map((poly, i) => (
          <polygon
            key={`poly-${i}`}
            points={poly.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill={poly.fill}
          />
        ))}
        {plan.rects.map((rect, i) => (
          <rect key={`rect-${i}`} x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill={rect.fill} />
        ))}
        {plan.lines.map((line, i) => (
          <line
            key={`line-${i}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={line.stroke}
            strokeWidth={0.004}
          />
        ))}
        {plan.labels.map((label, i) => (
          <text
            key={`label-${i}`}
            x={label.x}
            y={label.y}
            fill={label.fill}
            fontSize={label.size ?? 0.03}
            textAnchor={label.anchor ?? "start"}
            dominantBaseline="middle"
          >
            {label.text}
          </text>
        ))}
      </svg>
    </div>
  );
}

type Props = {
  layer: SurfLayer;
  theme: SurfStyle;
  selected: boolean;
  safeZone?: { x: number; y: number; w: number; h: number };
  otherLayers?: SurfLayer[];
  imageUrl?: string | null;
  containerRef: RefObject<HTMLDivElement | null>;
  onGuidesChange?: (guides: import("@vida/surf-core").GuideLine[]) => void;
  onSelect: (additive: boolean) => void;
  onRectChange: (rect: { x: number; y: number; w: number; h: number }) => void;
  onContentChange: (content: string) => void;
  onGraphicChange?: (graphic: import("@vida/surf-core").SurfGraphicData) => void;
};

export function EditorLayer({
  layer,
  theme,
  selected,
  safeZone,
  otherLayers = [],
  imageUrl,
  containerRef,
  onGuidesChange,
  onSelect,
  onRectChange,
  onContentChange,
  onGraphicChange,
}: Props) {
  const { onPointerDown } = useNormDrag({
    containerRef,
    rect: layer,
    safeZone,
    otherLayers,
    onChange: onRectChange,
    onGuidesChange,
  });

  const pendingTextRef = useRef<string | null>(null);

  useEffect(() => {
    pendingTextRef.current = null;
  }, [layer.id, selected]);

  const slideHeightPx = useSlideHeightPx(containerRef);
  const layoutCtx = useMemo(
    () => createWebTextLayoutContext(slideHeightPx),
    [slideHeightPx],
  );

  // Fit-to-box + shared line breaks (same engine as PDF export).
  const ts = resolveFittedTextStyle(layer, theme, layoutCtx);
  const render = layerRenderStyle(layer, theme, slideHeightPx);
  const displayText = applyTextCase(layer.content, ts.case);
  const textLines = useMemo(() => {
    if (layer.type !== "text" || !displayText.trim()) return [""];
    return layoutLayerTextLines(
      displayText,
      layer.w,
      ts.font,
      ts.weight,
      ts.sizeRel,
      ts.lineHeight,
      slideHeightPx,
      ts.maxLines,
      layoutCtx.measurer,
    );
  }, [layer.type, layer.w, displayText, ts, slideHeightPx, layoutCtx.measurer]);
  const snappedWeight = snapSurfFontWeight(ts.font, ts.weight);
  const textBoxBg = layer.type === "text" ? getTextBoxBackground(layer) : null;
  const showTextRaster = layer.type === "text" && isTextRasterLayer(layer);
  const rasterUrl = showTextRaster ? imageUrl : null;
  const rasterStale = showTextRaster && isTextRasterStale(layer);
  const rasterBackdrop =
    showTextRaster && rasterUrl && usesRasterSolidPanel(layer)
      ? getRasterBackdrop(layer, theme)
      : null;

  const boxStyle: CSSProperties = {
    left: `${layer.x * 100}%`,
    top: `${layer.y * 100}%`,
    width: `${layer.w * 100}%`,
    height: `${layer.h * 100}%`,
    zIndex: layer.z,
    transform: `rotate(${layer.rotation}deg)`,
    position: "absolute",
    boxSizing: "border-box",
  };

  return (
    <div
      className={selected ? "editor-layer selected" : "editor-layer"}
      style={boxStyle}
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelect(e.metaKey || e.ctrlKey || e.shiftKey);
      }}
    >
      {layer.type === "text" && (
        <>
          {showTextRaster && rasterUrl && (
            <div className="editor-layer-text-raster has-image">
              {rasterBackdrop && (
                <div
                  className="editor-layer-raster-backdrop"
                  style={{ backgroundColor: rasterBackdrop }}
                  aria-hidden
                />
              )}
              {(() => {
                const crop = getLayerCrop(layer);
                if (crop) {
                  return (
                    <img
                      src={rasterUrl}
                      alt={textRasterAltText(layer)}
                      draggable={false}
                      className="editor-layer-text-raster-img"
                      style={{
                        position: "absolute",
                        width: `${100 / crop.w}%`,
                        height: `${100 / crop.h}%`,
                        left: `${(-crop.x * 100) / crop.w}%`,
                        top: `${(-crop.y * 100) / crop.h}%`,
                        objectFit: "fill",
                      }}
                    />
                  );
                }
                return (
                  <img
                    src={rasterUrl}
                    alt={textRasterAltText(layer)}
                    draggable={false}
                    className="editor-layer-text-raster-img"
                  />
                );
              })()}
              {rasterStale && <span className="text-raster-stale-badge">Rigenera</span>}
              <span className="text-raster-mode-badge">Raster</span>
            </div>
          )}
          {showTextRaster && !rasterUrl && (
            <div className="editor-layer-text editor-layer-text--raster-placeholder">
              Illustrazione raster…
            </div>
          )}
          {!showTextRaster && textBoxBg && (
            <div
              className="editor-layer-text-box-bg"
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: textBoxBg.color,
                opacity: textBoxBg.opacity,
                borderRadius: 6,
                zIndex: 0,
              }}
            />
          )}
          {!showTextRaster && (
            <div
              className="editor-layer-text"
              contentEditable={selected}
              suppressContentEditableWarning
              onInput={(e) => {
                pendingTextRef.current = e.currentTarget.textContent ?? "";
              }}
              onBlur={() => {
                if (pendingTextRef.current != null) {
                  onContentChange(pendingTextRef.current);
                }
                pendingTextRef.current = null;
              }}
              style={{
                position: "relative",
                zIndex: 1,
                fontFamily: render.fontFamily,
                fontSize: `${(ts.sizeRel * 100).toFixed(1)}cqh`,
                fontWeight: snappedWeight,
                color: render.fillText,
                textAlign: render.align,
                lineHeight: render.lineHeight,
                letterSpacing: render.letterSpacing,
                textTransform: render.textTransform,
                width: "100%",
                height: "100%",
                outline: "none",
              }}
            >
              {selected
                ? displayText
                : textLines.map((line, i) => (
                    <span key={`${i}-${line}`} className="editor-layer-text-line">
                      {line || "\u00a0"}
                    </span>
                  ))}
            </div>
          )}
        </>
      )}
      {layer.type === "shape" && (
        <div
          className="editor-layer-shape"
          style={{
            width: "100%",
            height: "100%",
            background: render.fill ?? theme.palette.accent,
            borderRadius: 8,
            opacity: 0.85,
          }}
        />
      )}
      {layer.type === "image" && (
        <div
          className={
            imageUrl
              ? getLayerTextRender(layer) === "in_image"
                ? "editor-layer-image has-image baked-illustration"
                : "editor-layer-image has-image"
              : "editor-layer-image"
          }
        >
          {imageUrl ? (
            (() => {
              const crop = getLayerCrop(layer);
              const alt =
                getLayerTextRender(layer) === "in_image"
                  ? bakedTextAltText(getLayerBakedText(layer))
                  : "";
              if (crop) {
                // Oversize + translate the bitmap so the crop region maps onto the box.
                return (
                  <img
                    src={imageUrl}
                    alt={alt}
                    draggable={false}
                    style={{
                      position: "absolute",
                      width: `${100 / crop.w}%`,
                      height: `${100 / crop.h}%`,
                      left: `${(-crop.x * 100) / crop.w}%`,
                      top: `${(-crop.y * 100) / crop.h}%`,
                      objectFit: "fill",
                    }}
                  />
                );
              }
              return (
                <img
                  src={imageUrl}
                  alt={alt}
                  draggable={false}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              );
            })()
          ) : (
            <span>
              {getLayerTextRender(layer) === "in_image" ? "Illustrazione…" : layer.content ? "Immagine…" : "Immagine"}
            </span>
          )}
          {getLayerTextRender(layer) === "in_image" && imageUrl && (
            <span className="baked-illustration-badge">Testo nell&apos;immagine</span>
          )}
        </div>
      )}
      {layer.type === "table" && (
        <TableLayerPreview layer={layer} theme={theme} />
      )}
      {layer.type === "chart" && (
        <ChartLayerPreview layer={layer} theme={theme} />
      )}
      {layer.type === "graphic" && (
        <GraphicLayerPreview
          layer={layer}
          theme={theme}
          selected={selected}
          onGraphicChange={onGraphicChange}
        />
      )}

      {selected && (
        <>
          <div className="resize-handle nw" onPointerDown={onPointerDown("resize-nw")} />
          <div className="resize-handle ne" onPointerDown={onPointerDown("resize-ne")} />
          <div className="resize-handle sw" onPointerDown={onPointerDown("resize-sw")} />
          <div className="resize-handle se" onPointerDown={onPointerDown("resize-se")} />
          <div className="move-handle" onPointerDown={onPointerDown("move")} title="Trascina" />
        </>
      )}
    </div>
  );
}
